// ---- Click-to-sit (body poses, CP4) ----
// The client half of furniture seats: the world publishes sittable points
// (structure/define.ts `seat` hook → world.userData.seats), and a canvas click
// near one sends a `sit` intent — the SERVER validates range/occupancy, snaps
// us onto the seat (a `correction`), faces us outward, and applies the seatable
// pose (confirmed back through the owner-only `pose` echo, like every pose).
//
// Registration order is load-bearing (the interact.ts contract): initSeats is
// wired AFTER initCooking and BEFORE initFocus, so a seat click wins over
// click-to-focus but loses to pickup/interact/fishing/cook clicks — furniture
// is the least-urgent thing under the cursor. Seats are plain points, not
// meshes (furniture consolidates into merged structure geometry at build), so
// picking is ray-to-point distance, not a raycast hit.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { sendSit } from "./net.ts";
import { flashToast } from "./inventory.ts";
import { registerHoverProbe } from "./cursor.ts";

export type WorldSeat = { id: string; x: number; y: number; z: number; face: number };

let seats: WorldSeat[] = [];

/** Adopt the freshly built world's seat points (called from initWorld — swaps
 *  wholesale on a map transfer, same lifecycle as setInteractWorld). */
export function setSeatWorld(world: THREE.Object3D): void {
  seats = (world.userData.seats as WorldSeat[] | undefined) ?? [];
}

/** How close (in world units) the click ray must pass to a seat point. Seats
 *  are ~0.32-radius stool tops, so this is "clicked ON the furniture". */
const PICK_RAY_DIST = 0.55;
/** Client-side reach gate (server re-checks at 3.5 with slack). */
const SIT_REACH = 3.2;

const _ray = new THREE.Raycaster();
const _ndc = new THREE.Vector2();
const _pt = new THREE.Vector3();

function seatUnderRay(rc: THREE.Raycaster): WorldSeat | null {
  let best: WorldSeat | null = null;
  let bestD = PICK_RAY_DIST;
  for (const st of seats) {
    const d = rc.ray.distanceToPoint(_pt.set(st.x, st.y, st.z));
    if (d < bestD) { bestD = d; best = st; }
  }
  return best;
}

export function initSeats(camera: THREE.Camera, canvas: HTMLCanvasElement): void {
  // Click-to-sit is a world click target — light the pointer over seat points.
  registerHoverProbe((rc) => !!seats.length && !!seatUnderRay(rc));

  canvas.addEventListener("pointerdown", (e) => {
    if (!game.joined || e.button !== 0 || inputLocked() || !seats.length) return;
    _ndc.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1);
    _ray.setFromCamera(_ndc, camera);
    const best = seatUnderRay(_ray);
    if (!best) return; // not a furniture click — focus/others may claim it
    e.stopImmediatePropagation();
    const dx = game.player.position.x - best.x;
    const dz = game.player.position.z - best.z;
    if (Math.hypot(dx, dz) > SIT_REACH) { flashToast("Too far away"); return; }
    sendSit(best.id); // occupied/stale seats reject silently server-side
  });
}
